import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '../lib/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientViewMode, setClientViewModeState] = useState(
    localStorage.getItem('ofimex_client_view_mode') === 'true'
  );

  const setClientViewMode = (value) => {
    setClientViewModeState(value);
    if (value) {
      localStorage.setItem('ofimex_client_view_mode', 'true');
    } else {
      localStorage.removeItem('ofimex_client_view_mode');
    }
  };

  const loadFeatures = async () => {
    try {
      const token = localStorage.getItem('ofimex_token');
      if (!token) return;
      const res = await axios.get(`${API}/api/my-features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatures(res.data);
    } catch {
      // Default: all features enabled for backwards compatibility
      setFeatures(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('ofimex_token');
    const storedUser = localStorage.getItem('ofimex_user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('ofimex_user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('ofimex_token');
          localStorage.removeItem('ofimex_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
      loadFeatures();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await apiLogin(email, password);
    const { access_token, user: userData } = response.data;
    // Defensive cleanup: drop any leftover impersonation/view-as flags so
    // a client/admin can never accidentally inherit a super-admin banner.
    localStorage.removeItem('ofimex_impersonating');
    localStorage.removeItem('ofimex_original_admin');
    localStorage.removeItem('ofimex_view_as_org_id');
    localStorage.setItem('ofimex_token', access_token);
    localStorage.setItem('ofimex_user', JSON.stringify(userData));
    setUser(userData);
    // Load features after login
    setTimeout(loadFeatures, 100);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('ofimex_token');
    localStorage.removeItem('ofimex_user');
    localStorage.removeItem('ofimex_client_view_mode');
    localStorage.removeItem('ofimex_impersonating');
    localStorage.removeItem('ofimex_original_admin');
    localStorage.removeItem('ofimex_view_as_org_id');
    setUser(null);
    setFeatures(null);
    setClientViewModeState(false);
  };

  // ---- View As (super-admin browsing another org) ----
  const enterViewAs = async (orgId) => {
    const res = await axios.post(`${API}/api/super-admin/view-as/${orgId}`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('ofimex_token')}` },
    });
    localStorage.setItem('ofimex_token', res.data.access_token);
    const me = await getMe();
    setUser(me.data);
    localStorage.setItem('ofimex_user', JSON.stringify(me.data));
    return me.data;
  };

  const toggleViewAsEdit = async () => {
    const res = await axios.post(`${API}/api/super-admin/view-as/toggle-edit`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('ofimex_token')}` },
    });
    localStorage.setItem('ofimex_token', res.data.access_token);
    const me = await getMe();
    setUser(me.data);
    localStorage.setItem('ofimex_user', JSON.stringify(me.data));
    return me.data.view_as_edit_mode;
  };

  const exitViewAs = async () => {
    const res = await axios.post(`${API}/api/super-admin/view-as/exit`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('ofimex_token')}` },
    });
    localStorage.setItem('ofimex_token', res.data.access_token);
    const me = await getMe();
    setUser(me.data);
    localStorage.setItem('ofimex_user', JSON.stringify(me.data));
  };

  const refreshUser = async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem('ofimex_user', JSON.stringify(res.data));
      return res.data;
    } catch {
      return null;
    }
  };

  const hasFeature = (featureCode) => {
    // If features not loaded yet or null, assume all enabled (backwards compat)
    if (!features) return true;
    return features[featureCode] === true;
  };

  // Granular permissions (module + field level)
  const hasModule = (moduleCode) => {
    // Admins, master admins, super admins have access to everything
    if (!user) return false;
    if (user.role === 'admin' || user.is_master_admin || user.is_super_admin) return true;
    const perms = user.permissions?.modules;
    if (!perms) return true; // Default visible
    return perms[moduleCode] !== false;
  };

  const hasField = (fieldCode) => {
    if (!user) return false;
    if (user.role === 'admin' || user.is_master_admin || user.is_super_admin) return true;
    const perms = user.permissions?.fields;
    if (!perms) return true;
    return perms[fieldCode] !== false;
  };

  const isAdmin = user?.role === 'admin';
  const isOps = user?.role === 'ops';
  const isClient = user?.role === 'client';
  const canManage = isAdmin || isOps;
  const effectiveRole = clientViewMode && canManage ? 'client' : user?.role;
  const orgType = user?.organization_type || 'comercializadora';
  const isAgenteAduanal = orgType === 'agente_aduanal';
  const isComercializadora = orgType === 'comercializadora';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      refreshUser,
      isAdmin,
      isOps,
      isClient,
      canManage,
      clientViewMode,
      setClientViewMode,
      effectiveRole,
      features,
      hasFeature,
      hasModule,
      hasField,
      orgType,
      isAgenteAduanal,
      isComercializadora,
      // View As helpers (super-admin only)
      enterViewAs,
      toggleViewAsEdit,
      exitViewAs,
      isViewingAs: !!user?.view_as_org_id,
      viewAsOrgName: user?.view_as_org_name,
      viewAsEditMode: user?.view_as_edit_mode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
